/**
 * Compliance Ledger Card · v2.7.5 · Phase 2
 * ─────────────────────────────────────────
 * Live ledger of daily compliance fees aggregated from the
 * /api/admin/compliance/* endpoints. Auto-polls every 30s.
 *
 * Row flow (L→R, exact spec order):
 *   Date | Tech Fee | Franchise Fee | Tax | Grand Total | Status | Action
 *
 * Outstanding (unpaid) rows render in RED to flag liabilities.
 * Each row has a "MARK PAID" button → opens dialog with method dropdown +
 * note → audit-logged by the active super-user account.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import adminApi from "@/lib/adminApi";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, RotateCw, ScrollText, BadgeDollarSign, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const fmt$ = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const METHODS = [
  ["cash_app", "Cash App"],
  ["debit", "Debit Transaction"],
];


export default function ComplianceLedgerCard() {
  const [today, setToday] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [paying, setPaying] = useState(null);  // row being marked paid
  const [method, setMethod] = useState("cash_app");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [unpaidOnly, setUnpaidOnly] = useState(true);  // v2.7.5+ · default ON

  const reload = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([
        adminApi.get("/admin/compliance/today"),
        adminApi.get("/admin/compliance/ledger?days=60"),
      ]);
      setToday(a.data);
      setRows(b.data?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load compliance ledger");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    const id = setInterval(reload, 30_000);
    return () => clearInterval(id);
  }, [reload]);

  // v2.7.6 Audit · INSTANT reload on every completed trip — listens for the
  // WebSocket-fed `rtg:trip_completed` event so the ledger never lags.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onTripCompleted = () => reload();
    window.addEventListener("rtg:trip_completed", onTripCompleted);
    return () => window.removeEventListener("rtg:trip_completed", onTripCompleted);
  }, [reload]);

  const forceRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await adminApi.post("/admin/compliance/recalculate");
      toast.success(`Synced · ${data.newly_logged} new · ${data.candidates_scanned} scanned`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recalculate failed");
    } finally {
      setRefreshing(false);
    }
  }, [reload]);

  const openMarkPaid = (row) => {
    setPaying(row);
    setMethod("cash_app");
    setNote("");
  };

  const submitMarkPaid = async () => {
    if (!paying) return;
    setSubmitting(true);
    try {
      await adminApi.post(
        `/admin/compliance/ledger/${paying.date}/mark-paid`,
        { payment_method: method, note: note || undefined },
      );
      toast.success(`Marked ${paying.date} as paid`);
      setPaying(null);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mark-paid failed");
    } finally {
      setSubmitting(false);
    }
  };

  const summary = useMemo(() => {
    const unpaidTotal = rows
      .filter((r) => r.status !== "paid")
      .reduce((s, r) => s + (r.grand_total || 0), 0);
    const unpaidCount = rows.filter((r) => r.status !== "paid").length;
    const paidCount = rows.length - unpaidCount;
    return { unpaidTotal, unpaidCount, paidCount };
  }, [rows]);

  // v2.7.5+ · Apply the "show unpaid only" toggle.
  const visibleRows = useMemo(
    () => (unpaidOnly ? rows.filter((r) => r.status !== "paid") : rows),
    [rows, unpaidOnly],
  );

  return (
    <Card className="bg-zinc-900 border-amber-400/40 p-5" data-testid="compliance-ledger-card">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h3 className="font-bold flex items-center gap-2">
            <ScrollText className="h-4 w-4 text-amber-400" />
            COMPLIANCE LEDGER
          </h3>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            Tech&nbsp;$1.50 + Franchise&nbsp;$1.00 + Tax&nbsp;9.5% per ride · 24-hour
            aggregation blocks · auto-refresh every 30 s · isolated from operational tables.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {summary.unpaidCount > 0 && (
            <Badge
              className="bg-red-500/15 text-red-300 border-red-400/40"
              data-testid="cl-summary-unpaid"
            >
              {summary.unpaidCount} unpaid · {fmt$(summary.unpaidTotal)} outstanding
            </Badge>
          )}
          <label className="flex items-center gap-1.5 text-[11px] text-zinc-400 cursor-pointer select-none px-2 py-1 rounded border border-zinc-700 bg-zinc-950 hover:border-amber-400/40" data-testid="cl-unpaid-only-toggle">
            <input
              type="checkbox"
              checked={unpaidOnly}
              onChange={(e) => setUnpaidOnly(e.target.checked)}
              className="accent-amber-400"
              data-testid="cl-unpaid-only-checkbox"
            />
            <span className="font-bold uppercase tracking-wider text-[10px]">Show Unpaid Only</span>
            {summary.paidCount > 0 && (
              <span className="text-[10px] text-zinc-600">({summary.paidCount} paid hidden)</span>
            )}
          </label>
          <Button
            size="sm"
            variant="outline"
            onClick={forceRefresh}
            disabled={refreshing || loading}
            className="h-8 bg-zinc-950 border-zinc-700 hover:border-amber-400 hover:text-amber-400 text-[11px]"
            data-testid="cl-refresh"
          >
            {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (
              <><RotateCw className="h-3.5 w-3.5 mr-1" />REFRESH NOW</>
            )}
          </Button>
        </div>
      </div>

      {today && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4 text-center" data-testid="cl-today">
          <Stat label="TODAY RIDES"     value={today.totals.ride_count}                    mono />
          <Stat label="TECH FEE"        value={fmt$(today.totals.tech_fee_total)}          />
          <Stat label="FRANCHISE FEE"   value={fmt$(today.totals.franchise_fee_total)}     />
          <Stat label="TAX (9.5%)"      value={fmt$(today.totals.tax_total)}               />
          <Stat label="GRAND TODAY"     value={fmt$(today.totals.grand_total)} highlight   />
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-10 text-zinc-500" data-testid="cl-loading">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Loading ledger…
        </div>
      ) : visibleRows.length === 0 ? (
        <div className="text-center py-8 text-zinc-500 text-sm" data-testid="cl-empty">
          {unpaidOnly && rows.length > 0
            ? `All ${rows.length} day-block${rows.length === 1 ? "" : "s"} are settled. Toggle "Show Unpaid Only" off to see paid history.`
            : "No compliance entries yet. As soon as rides are marked completed, daily blocks will appear here."}
        </div>
      ) : (
        <div className="overflow-x-auto" data-testid="cl-table-wrap">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <tr>
                <th className="text-left p-2">Date</th>
                <th className="text-right p-2">Tech Fee</th>
                <th className="text-right p-2">Franchise Fee</th>
                <th className="text-right p-2">Tax (9.5%)</th>
                <th className="text-right p-2">Grand Total</th>
                <th className="text-center p-2">Rides</th>
                <th className="text-center p-2">Status</th>
                <th className="text-right p-2">Action</th>
              </tr>
            </thead>
            <tbody data-testid="cl-rows">
              {visibleRows.map((r) => {
                const unpaid = r.status !== "paid";
                const cls = unpaid ? "text-red-400" : "text-zinc-200";
                return (
                  <tr
                    key={r.date}
                    className={`border-b border-zinc-800 hover:bg-zinc-950/60 ${cls}`}
                    data-testid={`cl-row-${r.date}`}
                  >
                    <td className="p-2 font-mono text-xs">{r.date}</td>
                    <td className={`p-2 text-right font-mono ${cls}`}>{fmt$(r.tech_fee_total)}</td>
                    <td className={`p-2 text-right font-mono ${cls}`}>{fmt$(r.franchise_fee_total)}</td>
                    <td className={`p-2 text-right font-mono ${cls}`}>{fmt$(r.tax_total)}</td>
                    <td className={`p-2 text-right font-mono font-bold ${cls}`}>{fmt$(r.grand_total)}</td>
                    <td className="p-2 text-center">
                      <Badge variant="outline" className="border-zinc-700 text-[10px]">{r.ride_count}</Badge>
                    </td>
                    <td className="p-2 text-center">
                      {unpaid ? (
                        <Badge className="bg-red-500/15 text-red-300 border-red-400/40 text-[10px]" data-testid={`cl-status-${r.date}`}>UNPAID</Badge>
                      ) : (
                        <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-400/40 text-[10px]" data-testid={`cl-status-${r.date}`}>
                          <CheckCircle2 className="h-3 w-3 mr-1" /> PAID
                        </Badge>
                      )}
                    </td>
                    <td className="p-2 text-right">
                      {unpaid ? (
                        <Button
                          size="sm"
                          onClick={() => openMarkPaid(r)}
                          className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold text-[11px] h-7"
                          data-testid={`cl-pay-${r.date}`}
                        >
                          <BadgeDollarSign className="h-3 w-3 mr-1" />MARK PAID
                        </Button>
                      ) : (
                        <span className="text-[10px] text-zinc-500" title={r.paid_at}>
                          {r.payment_method?.replace("_", " ").toUpperCase()} · {r.paid_by_name}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!paying} onOpenChange={(o) => !o && setPaying(null)}>
        <DialogContent className="bg-zinc-900 border-amber-400/40" data-testid="cl-pay-dialog">
          <DialogHeader>
            <DialogTitle>Mark {paying?.date} as PAID</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="text-xs text-zinc-400">
              Settling <span className="font-bold text-amber-400">{fmt$(paying?.grand_total)}</span>
              {" "}for {paying?.ride_count} rides. Action will be audit-logged.
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500">Payment Method</label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger className="bg-zinc-950 border-zinc-700 mt-1" data-testid="cl-pay-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {METHODS.map(([v, l]) => (
                    <SelectItem key={v} value={v}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500">Note (optional)</label>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Settlement ref · receipt # · ..."
                className="bg-zinc-950 border-zinc-700 mt-1"
                data-testid="cl-pay-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaying(null)} className="bg-zinc-950 border-zinc-700">Cancel</Button>
            <Button onClick={submitMarkPaid} disabled={submitting} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold" data-testid="cl-pay-confirm">
              {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "CONFIRM PAID"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


function Stat({ label, value, mono, highlight }) {
  return (
    <div className={`rounded border ${highlight ? "border-amber-400/40 bg-amber-400/5" : "border-zinc-800 bg-zinc-950"} p-2`}>
      <div className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`mt-1 ${mono ? "font-mono" : "font-bold"} ${highlight ? "text-amber-400" : "text-zinc-100"}`}>
        {value}
      </div>
    </div>
  );
}
